import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { authFetch } from '../services/authFetch';

const RekapPage = () => {
  const navigate = useNavigate();
  const [recentRequests, setRecentRequests] = useState([]);
  const [requestsLoading, setRequestsLoading] = useState(true);
  const [user, setUser] = useState(null);

  // Check student access
  useEffect(() => {
    const userData = localStorage.getItem('user');
    const userRole = localStorage.getItem('userRole');
    const isAuthenticated = localStorage.getItem('isAuthenticated');
    
    if (userData && isAuthenticated === 'true') {
      const parsedUser = JSON.parse(userData);
      setUser(parsedUser);
      
      // Check if user is student
      if (parsedUser.role !== 'student' && userRole !== 'student') {
        navigate('/');
        return;
      }
    } else {
      navigate('/login');
      return;
    }
  }, [navigate]);

  // Fetch recent requests from individual endpoints
  useEffect(() => {
    const fetchRecentRequests = async () => {
      setRequestsLoading(true);
      try {
        console.log('Fetching recent requests from multiple endpoints...');
        const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/';
        
        // Fetch from all individual endpoints
        const endpoints = [
          { url: `${baseUrl}api/interuption_permits`, type: 'ijin_interupsi' },
          { url: `${baseUrl}api/absent_permits`, type: 'ijin_tidakmasuk' },
          { url: `${baseUrl}api/earlyleave_permits`, type: 'ijin_pulangawal' },
          { url: `${baseUrl}api/kersos_reports`, type: 'laporan_kersos' },
          { url: `${baseUrl}api/overtime_reports`, type: 'laporan_lembur' },
          { url: `${baseUrl}api/psm_reports`, type: 'laporan_psm' }
        ];

        const fetchPromises = endpoints.map(async (endpoint) => {
          try {
            console.log(`Fetching from: ${endpoint.url}`);
            const response = await authFetch(endpoint.url, {
              method: 'GET',
              headers: { 'Accept': 'application/json' }
            });
            
            if (response.ok) {
              const data = await response.json();
              console.log(`Data from ${endpoint.type}:`, data);
              
              // Extract the array from different possible response formats
              let items = [];
              if (data.data && Array.isArray(data.data)) {
                items = data.data;
              } else if (Array.isArray(data)) {
                items = data;
              } else if (data.items && Array.isArray(data.items)) {
                items = data.items;
              }
              
              // Add type information to each item
              return items.map(item => ({
                ...item,
                type: endpoint.type,
                source_endpoint: endpoint.url
              }));
            } else {
              console.warn(`Failed to fetch from ${endpoint.url}: ${response.status}`);
              return [];
            }
          } catch (error) {
            console.error(`Error fetching from ${endpoint.url}:`, error);
            return [];
          }
        });

        // Wait for all requests to complete
        const results = await Promise.all(fetchPromises);
        
        // Combine all results
        const allRequests = results.flat();
        console.log('All combined requests:', allRequests);
        
        // Sort by date (newest first)
        const sortedRequests = allRequests
          .filter(item => item && item.id) // Filter out invalid items
          .sort((a, b) => {
            const dateA = new Date(a.created_at || a.tanggal || a.updated_at || 0);
            const dateB = new Date(b.created_at || b.tanggal || b.updated_at || 0);
            return dateB - dateA; // Newest first
          });

        console.log('Sorted requests:', sortedRequests);
        
        const mappedRequests = mapRequestsToActivities(sortedRequests);
        console.log('Final mapped requests:', mappedRequests);
        setRecentRequests(mappedRequests);
        
      } catch (error) {
        console.error('Failed to fetch recent requests:', error);
        setRecentRequests([]);
      } finally {
        setRequestsLoading(false);
      }
    };

    // Only fetch requests if user is authenticated as student
    const userRole = localStorage.getItem('userRole');
    
    if (user?.role === 'student' || userRole === 'student') {
      fetchRecentRequests();
    }
  }, [user]);

  // Helper function to map API response to activity format
  const mapRequestsToActivities = (requests) => {
    console.log('Mapping requests:', requests);
    
    if (!Array.isArray(requests)) {
      console.warn('Requests is not an array:', requests);
      return [];
    }

    return requests.map(request => {
      console.log('Processing request:', request);
      
      const type = request.type || request.table_name || request.form_type;
      // Get the actual status from the database (request_status field)
      const actualStatus = request.request_status || request.status || 'pending';
      let formType, title, mappedData;

      // Determine form type and title based on request type, and map data appropriately
      switch (type) {
        case 'ijin_interupsi':
          formType = 'interupsi';
          title = `Permohonan Interupsi ${getStatusText(actualStatus)}`;
          // Map data structure for InterupsiForm
          mappedData = {
            classType: request.current_activity || 'Teori',
            departureTime: request.leave_hour || request.departure_time || '',
            returnTime: request.return_hour || request.return_time || '',
            purpose: request.purpose || '',
            destination: request.destination || '',
            responsible: request.pic || request.responsible || '',
            relationship: request.relationship || '',
            phoneNumber: request.pic_contact_number || request.phone_number || '',
            ...request // keep all original fields for fallback
          };
          break;
        case 'ijin_pulangawal':
          formType = 'pulang-awal';
          title = `Permohonan Pulang Awal ${getStatusText(actualStatus)}`;
          // LeaveForm uses the same field names as API response
          mappedData = {
            earlyleave_time: request.earlyleave_time || '',
            purpose: request.purpose || '',
            destination: request.destination || '',
            pic: request.pic || '',
            relationship: request.relationship || '',
            pic_contact_number: request.pic_contact_number || '',
            ...request // keep all original fields for fallback
          };
          break;
        case 'ijin_tidakmasuk':
          formType = 'tidak-masuk';
          title = `Permohonan Tidak Masuk ${getStatusText(actualStatus)}`;
          // SicknessForm uses the same field names as API response
          mappedData = {
            from_date: request.from_date || '',
            to_date: request.to_date || '',
            purpose: request.purpose || '',
            destination: request.destination || '',
            pic: request.pic || '',
            relationship: request.relationship || '',
            pic_contact_number: request.pic_contact_number || '',
            images: request.images || [],
            ...request // keep all original fields for fallback
          };
          break;
        case 'laporan_kersos':
          formType = 'kersos';
          title = `Laporan Kersos ${getStatusText(actualStatus)}`;
          // Map data structure for KersosForm
          mappedData = {
            activity_date: request.activity_date || request.kersos_date || '',
            start_time: request.start_time || '',
            finish_time: request.finish_time || request.end_time || '',
            violation: request.violation || '',
            reflection: request.reflection || '',
            ...request // keep all original fields for fallback
          };
          break;
        case 'laporan_lembur':
          formType = 'kompensasi';
          title = `Laporan Lembur ${getStatusText(actualStatus)}`;
          // Map data structure for KompensasiForm
          mappedData = {
            overtime_date: request.activity_date || request.overtime_date || '',
            start_time: request.start_time || '',
            finish_time: request.finish_time || request.end_time || '',
            purpose: request.purpose || request.violation || '',
            type_or_work: request.type_or_work || request.activity_description || '',
            reflection: request.reflection || '',
            teacher_name: request.teacher_name || request.supervisor || '',
            images: request.images || [],
            ...request // keep all original fields for fallback
          };
          break;
        case 'laporan_psm':
          formType = 'psm';
          title = `Laporan PSM ${getStatusText(actualStatus)}`;
          // Map data structure for PSMForm
          mappedData = {
            psm_date: request.psm_date || request.activity_date || '',
            start_time: request.start_time || '',
            finish_time: request.finish_time || request.end_time || '',
            apresiasis_id: request.apresiasis_id || '',
            description: request.description || request.activity_description || '',
            reflection: request.reflection || '',
            pic: request.pic || request.supervisor || '',
            pic_contact_number: request.pic_contact_number || request.phone_number || '',
            images: request.images || [],
            ...request // keep all original fields for fallback
          };
          break;
        default:
          formType = type || 'unknown';
          title = `Request ${getStatusText(actualStatus)}`;
          mappedData = request;
      }

      const mappedRequest = {
        id: request.id,
        date: request.created_at || request.tanggal || request.updated_at || new Date().toISOString(),
        title: title,
        type: type && type.startsWith('ijin_') ? 'surat-ijin' : 'laporan',
        formType: formType,
        status: actualStatus,
        statusText: getStatusText(actualStatus),
        data: mappedData
      };
      
      console.log('Mapped request:', mappedRequest);
      return mappedRequest;
    });
  };

  // Helper function to convert status to display text
  const getStatusText = (status) => {
    switch (status) {
      case 'approved':
      case 'disetujui':
        return 'Disetujui';
      case 'rejected':
      case 'ditolak':
        return 'Ditolak';
      case 'pending':
      case 'diproses':
        return 'Diproses';
      case 'menunggu':
        return 'Menunggu';
      default:
        return 'Diajukan';
    }
  };

  // Handle view activity - redirect to appropriate form with data
  const handleViewActivity = (activity) => {
    if (activity.type === 'surat-ijin') {
      // For permission letters (surat-ijin)
      navigate('/surat-ijin', { 
        state: { 
          viewingHistoricalRequest: {
            id: activity.id,
            type: activity.title,
            date: activity.date,
            status: activity.status,
            statusText: activity.statusText,
            formType: activity.formType,
            data: activity.data
          } 
        } 
      });
    } else if (activity.type === 'laporan') {
      // For reports (laporan)
      navigate('/laporan', { 
        state: { 
          viewingHistoricalRequest: {
            id: activity.id,
            type: activity.title,
            date: activity.date,
            status: activity.status,
            statusText: activity.statusText,
            formType: activity.formType,
            data: activity.data
          } 
        } 
      });
    }
  };

  // Helper function to format dates
  const formatDate = (dateString) => {
    const options = { month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  return (
    <PageContainer>
      <PageHeader>
        <BackButton onClick={() => navigate('/pelayanan')}>
          <span>←</span> Kembali
        </BackButton>
        <h1>Rekap Request</h1>
        <p>Lihat semua request yang pernah dibuat</p>
      </PageHeader>
      
      <ContentContainer>
        <ActivityCard>
          <h3>Semua Request</h3>
          {requestsLoading ? (
            <LoadingContainer>
              <LoadingIcon>📊</LoadingIcon>
              <LoadingText>Memuat data request...</LoadingText>
            </LoadingContainer>
          ) : recentRequests.length === 0 ? (
            <EmptyState>
              <EmptyIcon>📋</EmptyIcon>
              <EmptyStateText>Belum ada request yang dibuat</EmptyStateText>
            </EmptyState>
          ) : (
            <ActivityList>
              {recentRequests.map((activity, index) => (
                <ActivityItem key={index} type={activity.type}>
                  <ActivityContent>
                    <ActivityDate>{formatDate(activity.date)}</ActivityDate>
                    <ActivityTitle>{activity.title}</ActivityTitle>
                    <ActivityStatus status={activity.status}>
                      {activity.statusText}
                    </ActivityStatus>
                  </ActivityContent>
                  <ViewButton onClick={() => handleViewActivity(activity)}>
                    View
                  </ViewButton>
                </ActivityItem>
              ))}
            </ActivityList>
          )}
        </ActivityCard>
      </ContentContainer>
    </PageContainer>
  );
};

const PageContainer = styled.div`
  padding: 1.5rem;
  max-width: 1000px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    padding: 1rem 0.5rem;
  }
`;

const PageHeader = styled.div`
  margin-bottom: 2rem;
  position: relative;
  
  h1 {
    font-size: 1.75rem;
    font-weight: 600;
    color: var(--text-dark);
    margin-bottom: 0.5rem;
    text-align: center;
  }
  
  p {
    color: var(--text-light);
    text-align: center;
    font-size: 0.95rem;
  }
`;

const BackButton = styled.button`
  background: transparent;
  border: none;
  display: flex;
  align-items: center;
  color: var(--primary);
  font-weight: 500;
  cursor: pointer;
  padding: 0.5rem 0.75rem;
  margin-bottom: 1rem;
  border-radius: 4px;
  transition: all 0.2s ease;
  
  span {
    margin-right: 0.5rem;
    font-size: 1.2rem;
  }
  
  &:hover {
    background-color: var(--primary);
    color: white;
  }
`;

const ContentContainer = styled.div`
  margin-bottom: 2rem;
`;

const ActivityCard = styled.div`
  background-color: var(--background);
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: var(--box-shadow);
  transition: background-color 0.3s, box-shadow 0.3s;
  
  h3 {
    font-size: 1.125rem;
    font-weight: 600;
    margin-bottom: 1rem;
    padding-bottom: 0.5rem;
    border-bottom: 1px solid var(--border);
    color: var(--text-dark);
  }
`;

const ActivityList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const ActivityItem = styled.div`
  padding: 1rem 1.25rem;
  border-radius: 12px;
  background-color: ${props => {
    const isDark = document.documentElement.getAttribute('data-theme') === 'dark';
    if (isDark) {
      switch(props.type) {
        case 'surat-ijin': return '#2c3b56'; // dark blue for dark mode
        case 'laporan': return '#1e3a32';    // dark green for dark mode
        default: return '#333333';
      }
    } else {
      switch(props.type) {
        case 'surat-ijin': return '#e8f1fd'; // light blue for light mode
        case 'laporan': return '#e6f8f5';    // light green for light mode
        default: return '#f5f5f5';
      }
    }
  }};
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: transform 0.2s, box-shadow 0.2s, background-color 0.3s;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: var(--box-shadow);
  }
`;

const ActivityContent = styled.div`
  flex: 1;
`;

const ActivityDate = styled.div`
  font-size: 0.75rem;
  color: var(--text-lighter);
  margin-bottom: 0.25rem;
`;

const ActivityTitle = styled.div`
  font-weight: 500;
  color: var(--text-dark);
  margin-bottom: 0.25rem;
`;

const ActivityStatus = styled.div`
  font-size: 0.8rem;
  font-weight: 500;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  display: inline-block;
  color: ${props => {
    switch(props.status) {
      case 'approved':
      case 'disetujui':
        return '#27ae60';
      case 'rejected':
      case 'ditolak':
        return '#e74c3c';
      case 'pending':
      case 'diproses':
      case 'menunggu':
        return '#f39c12';
      default:
        return '#95a5a6';
    }
  }};
  background-color: ${props => {
    switch(props.status) {
      case 'approved':
      case 'disetujui':
        return '#d5f4e6';
      case 'rejected':
      case 'ditolak':
        return '#fdeaea';
      case 'pending':
      case 'diproses':
      case 'menunggu':
        return '#fef9e7';
      default:
        return '#f8f9fa';
    }
  }};
`;

const ViewButton = styled.button`
  background: var(--primary, #3354a5);
  color: white;
  border: none;
  border-radius: 6px;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s, transform 0.2s;
  
  &:hover {
    background: #2a4580;
    transform: translateY(-1px);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  border-radius: 12px;
  background-color: rgba(43, 93, 161, 0.05);
`;

const LoadingIcon = styled.span`
  font-size: 2rem;
  color: var(--primary);
  margin-bottom: 1rem;
`;

const LoadingText = styled.div`
  color: var(--text-dark);
  font-weight: 500;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  border-radius: 12px;
  background-color: rgba(43, 93, 161, 0.05);
`;

const EmptyIcon = styled.span`
  font-size: 2rem;
  color: var(--primary);
  margin-bottom: 1rem;
`;

const EmptyStateText = styled.div`
  color: var(--text-dark);
  font-weight: 500;
  text-align: center;
`;

export default RekapPage;